import React, { useState, useEffect, useRef } from 'react';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firestore } from './firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import './Chat.css';

const Chat = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [bannedWordWarning, setBannedWordWarning] = useState('');
    const bannedWords = ['instagram', 'whatsapp', 'telegram', 'snapchat'];
    const user = auth.currentUser;
    const messagesEndRef = useRef(null);
    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        if (user) {
            const q = query(collection(firestore, "conversations"), where("vendorId", "==", user.uid), orderBy("updatedAt", "desc"));
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const convos = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setConversations(convos);
                setLoading(false);

                // Auto-open popover on mobile if no conversation is selected
                if (isMobile && !selectedConversation && convos.length > 0) {
                    setIsPopoverOpen(true);
                }
            });
            return () => unsubscribe();
        }
    }, [user, isMobile, selectedConversation]);

    useEffect(() => {
        if (selectedConversation) {
            const q = query(collection(firestore, "conversations", selectedConversation.id, "messages"), orderBy("timestamp", "asc"));
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const msgs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMessages(msgs);
            });
            return () => unsubscribe();
        }
    }, [selectedConversation]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleConversationSelect = (convo) => {
        setSelectedConversation(convo);
        setIsPopoverOpen(false);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const text = newMessage.trim();
        if (!text) return;

        const lowerText = text.toLowerCase();
        const foundBanned = bannedWords.find(word => lowerText.includes(word));
        if (foundBanned) {
            setBannedWordWarning(`Please keep discussions on AYLA.`);
            return;
        }

        setBannedWordWarning('');

        const functions = getFunctions();
        const sendMessage = httpsCallable(functions, 'sendMessage');

        try {
             await sendMessage({
                 conversationId: selectedConversation.id,
                 senderId: user.uid,
                 text,
             });
             setNewMessage('');
         } catch (error) {
             console.error("Error sending message:", error);
         }
     };

    if (loading) {
        return <div className="loading-spinner-container"><div className="loading-spinner"></div></div>;
    }

    const filteredConversations = conversations.filter(convo =>
        (convo.shopifyCustomerName && convo.shopifyCustomerName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (convo.shopifyClientEmail && convo.shopifyClientEmail.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const conversationListComponent = (isPopover = false) => (
        <>
            <div className="conversations-header">
                {isPopover ? (
                    <button className="popover-close-btn" onClick={() => setIsPopoverOpen(false)}>&times;</button>
                ) : null}
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="conversation-search-input"
                />
            </div>
            {loading ? (
                <div className="no-conversations">
                    <p>Loading...</p>
                </div>
            ) : filteredConversations.length > 0 ? (
                filteredConversations.map(convo => (
                    <div
                        key={convo.id}
                        className={`conversation-item ${selectedConversation?.id === convo.id ? 'selected' : ''}`}
                        onClick={() => handleConversationSelect(convo)}
                    >
                        <p className="customer-id">{convo.shopifyClientEmail}{convo.shopifyCustomerName ? ` (${convo.shopifyCustomerName})` : ''}</p>
                        <p className="last-message">{convo.lastMessage}</p>
                    </div>
                ))
            ) : (
                <div className="no-conversations">
                    <p>No conversations yet, wait for a customer to message you</p>
                </div>
            )}
        </>
    );

    return (
        <div className="chat-container">
            {isPopoverOpen && isMobile && (
                <div className="conversation-switcher-popover">
                    <div className="popover-conversations-list">
                        {conversationListComponent(true)}
                    </div>
                </div>
            )}

            <div className="conversations-list desktop-only">
                {conversationListComponent()}
            </div>

            <div className="messages-view">
                {selectedConversation ? (
                    <>
                        <div className="messages-header">
                             <h3>{selectedConversation.shopifyClientEmail}{selectedConversation.shopifyCustomerName ? ` (${selectedConversation.shopifyCustomerName})` : ''}</h3>
                             <button className="mobile-switcher-btn" onClick={() => setIsPopoverOpen(true)}>
                                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.67 3.87L9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z"/></svg>
                             </button>
                        </div>
                        <div className="messages-list">
                            {messages.map(msg => (
                                <div key={msg.id} className={`message-item-container ${msg.senderId === user.uid ? 'sent' : 'received'}`}>
                                    <div className={`message-item`}>
                                        <p>{msg.text}</p>
                                    </div>
                                    {msg.timestamp && (
                                        <div className="message-timestamp">
                                            {new Date(msg.timestamp.seconds * 1000).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                        <form className="message-input-form" onSubmit={handleSendMessage}>
                            {bannedWordWarning && (
                                <div style={{ position: 'absolute', top: '-22px', left: '16px', right: '16px', color: 'red', fontSize: '0.85rem' }}>
                                    {bannedWordWarning}
                                </div>
                            )}
                             <input
                                 type="text"
                                 value={newMessage}
                                 onChange={(e) => setNewMessage(e.target.value)}
                                 placeholder="Type a message..."
                             />
                            <button type="submit" title="Send">
                                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="no-conversation-selected">
                        <p>Select a conversation to start chatting.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Chat;
